import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useHeader } from "../context/HeaderContext.jsx";
import Footer from "../components/Footer.jsx";
import { dummyFetch } from "../utils/dummyListings.js";
import { formatDisplayPrice } from "../utils/currency.js";
import ProductCard from "../components/ProductCard.jsx";
import { isTouchDevice } from "../utils/viewport.js";
import {
  fetchItems,
  fetchSoldItems,
  fetchBrandBackgrounds,
  getCachedItems,
  getShowcaseHiddenBrands,
  warmupAll,
  cancelWarmup,
} from "../utils/inventoryWarmup.js";
import { parseDateSafe } from "../utils/time.js";
import { coverOf, rankHits } from "../utils/searchHits.js";
import { runSearchFill } from "../utils/searchFill.js";
import { preloadCatalog } from "./FeaturedPage.jsx";
import { createGlider } from "../utils/glideScroll.js";
import { useMediaQuery } from "../hooks/useMediaQuery.js";
import { SELF_SCROLL_ATTR } from "../utils/selfScroll.js";
import { hasMoreBelow } from "../utils/scrollCue.js";
import { requestBrandNav } from "../utils/brandNavSignal.js";
import { CATEGORY_GROUPS, categoryOf } from "../utils/itemCategories.js";


// The landing page is a hub: it lands and sends people on. NEW ARRIVALS
// first, as a static grid of the newest
// six in the catalog's own cards; then tiles that each go somewhere: the
// brands with the most stock as image tiles (into /brand, landing on that
// brand), SHOP BY category tiles (into the catalog filtered by ?cat=) and
// the sold archive; then one SHOP button into the whole catalog. Section
// headers are the brand row's own classes so they track the brand panels.
// The site header sits above it all in flow (sticky, as on the catalog).

// The twelve newest pieces, one per cell; the row drifts through them on
// its own, round and round (the arrivals effect below). Was 10: nine
// products and a See-more tile.
const NEW_ARRIVALS_COUNT = 12;
// The drift's pace: one card's width every this many seconds.
const ARRIVALS_SECONDS_PER_CARD = 7;
// How long NEW ARRIVALS stays held after a finger lifts off it.
const ARRIVALS_TOUCH_RESUME_MS = 1200;
// An arrow press moves the row this many cards, over this long.
const ARRIVALS_NUDGE_CARDS = 2;
const ARRIVALS_NUDGE_MS = 480;
// After the glide the row rests this long, then picks the drift back up at
// ARRIVALS_RAMP_FROM of its pace and eases to the full pace over
// ARRIVALS_RAMP_MS (the owner, 2026-10-17: "momentarily stop ... then start
// again a bit slower than the current cadence then follow into the current
// cadence"; then "do 1s and make the transition from no move to move quicker
// and cleaner": a 1s rest, and the pick-up eased from a standstill over 0.7s
// rather than jumping to half the pace and taking 2s to the rest).
const ARRIVALS_NUDGE_REST_MS = 1000;
const ARRIVALS_RAMP_FROM = 0;
const ARRIVALS_RAMP_MS = 700;
// The landing's scroll-down caret (the scroll-cue effect below): how long it
// waits after an arrival before it first shows, and how long the page has to
// be still after a scroll before it comes back.
const SCROLL_CUE_DELAY_MS = 1000;
const SCROLL_CUE_IDLE_MS = 2000;
// Where a press of it lands a section: its title this far under the
// header's height. The framing the user pointed at, SHOWCASE at 1915x786
// with its title 27px under the 48px header, which puts the whole section
// in view: title, tiles and SEE MORE.
const SECTION_TITLE_INSET = 27;
// How many rows the list under the search bar holds: SUGGESTED while the
// field is empty, RESULTS once something is typed. Eight, two rows of four
// cards on desktop (the owner, 2026-10-25, as the search overlay's grid; it
// was 6 as a list); phones show the first four (app.css), clear of the
// keyboard.
const SUGGEST_ROWS = 8;
// The longest SUGGESTED waits for the popularity ranking that picks its four:
// after this a slow ranking is given up on and the catalog's order stands.
// The 3s the picks always used to wait, so no load comes in later than it did.
const PICKS_RANKING_CAP_MS = 3000;
// No cap: the row scrolls, so every brand with stock is reachable by dragging
// it — the same way the arrivals row works. Capping it at four hid the rest
// behind a row that looked complete.

// Newest first, by the same field the catalog's "Newly Arrived" sort uses.
const listedAt = (item) => parseDateSafe(item.created_at || item.updated_at) ?? 0;
const soldAt = (item) => parseDateSafe(item.sold_at || item.updated_at || item.created_at) ?? 0;
// coverOf comes from utils/searchHits, shared with the search overlay.
// Tiles the showcase shows before "See more" is pressed. One row's worth, so
// the collapsed state reads as a row rather than a truncated grid: three
// across from 768px up (the owner, 2026-10-17: "make the boxes bigger maybe
// 3 instead of 4"), and a 2 x 2 block of four on a phone.
const SHOWCASE_COLLAPSED_WIDE = 3;
const SHOWCASE_COLLAPSED_PHONE = 4;

// A tile row's first arrival: hidden ("pre") until the row first comes into
// view, then "in" while its tiles fade in left to right (app.css,
// .home-tiles--in, a beat apart by --i), then "done", so tiles added later
// (SHOWCASE's SEE MORE, which has its own entrance) do not replay it. Shown
// at once under prefers-reduced-motion or without an IntersectionObserver.
// `ready` is when the row exists to be watched (its data is in).
const TILE_REVEAL_STEP_MS = 90;
const TILE_REVEAL_MS = 600;
function useTileReveal(ref, ready, count) {
  const [phase, setPhase] = useState(() => (
    typeof window === "undefined"
    || typeof IntersectionObserver === "undefined"
    || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
      ? "done"
      : "pre"
  ));
  useEffect(() => {
    if (phase !== "pre" || !ready) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      setPhase("in");
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, ready, phase]);
  useEffect(() => {
    if (phase !== "in") return undefined;
    const t = setTimeout(() => setPhase("done"), TILE_REVEAL_MS + Math.max(0, count - 1) * TILE_REVEAL_STEP_MS + 100);
    return () => clearTimeout(t);
  }, [phase, count]);
  return phase === "done" ? "" : ` home-tiles--${phase}`;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// One tile: an image under a label and a count, the whole thing the link.
// Brand backgrounds are photos and fill the tile; product images are
// cut-outs on black and are fitted, not cropped (--product). No image at
// all leaves a plain dark tile with the label.
function Tile({ to, onClick, image, product, label, sub, index = 0 }) {
  const cls = `home-tile${image ? "" : " home-tile--plain"}${product ? " home-tile--product" : ""}`;
  // Its place in the row, for the row's first fade-in (useTileReveal).
  const style = { "--i": index };
  const body = (
    <>
      <span
        className="home-tile__media"
        style={image ? { backgroundImage: `url(${image})` } : undefined}
        aria-hidden="true"
      />
      <span className="home-tile__label">
        {label}
        {sub && <span className="home-tile__sub">{sub}</span>}
      </span>
    </>
  );
  if (to) {
    return <Link to={to} className={cls} style={style}>{body}</Link>;
  }
  return <button type="button" className={cls} style={style} onClick={onClick}>{body}</button>;
}

// Resolves once an image has loaded, or failed, so the search's fill can
// wait for the photo the next page opens on; the page then has it from the
// browser's cache.
function preloadImage(url) {
  if (!url) return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
}

// A piece's size as its card shows it (ProductCard's sizeLabelOf): the
// inventory stores size and colour as one CSV ("44 (fits like M), Black"),
// and the size is the first part.
function sizeLabelOf(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  return s.includes(",") ? s.split(",")[0].trim() : s;
}

// A row's photo, contained in a fixed 48px square (app.css
// .home-suggest__row-img), so a wide belt and a tall vest line up.
function SuggestRowImage({ url }) {
  return (
    <span
      className="home-suggest__row-img"
      style={url ? { backgroundImage: `url(${url})` } : undefined}
      aria-hidden="true"
    />
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { setLoading } = useHeader();
  const [data, setData] = useState(() => getCachedItems());
  const [dataLoading, setDataLoading] = useState(() => !getCachedItems());
  const [soldData, setSoldData] = useState([]);
  const [brandBgs, setBrandBgs] = useState({});
  // The brands the owner hid from the Showcase (site manager's per-brand
  // switch), or null until known. It comes with the backdrops; the SHOWCASE
  // tiles wait for it, so a hidden brand's tile never shows and then goes.
  const [hiddenBrands, setHiddenBrands] = useState(() => getShowcaseHiddenBrands());
  // The landing search field's text. Submitting hands off to the same
  // /featured/search route the header's modal uses.
  const [homeQuery, setHomeQuery] = useState("");
  // Submitting fills the rule under the field left to right before the
  // results load — the sidebar's search animation, run on the LINE rather
  // than the letters. Written to a CSS custom property on the form, so the
  // ticker never re-renders the page it is animating over.
  const [searchFilling, setSearchFilling] = useState(false);
  // Whether the search has focus: the field, its button, or a row of the
  // list under it (tracked on the column, so tabbing from the field down
  // the rows keeps the list open). The list is the search's focus state
  // (the owner, 2026-09-30): at rest only the bar shows; pressing into it
  // drops SUGGESTED under it, and typing turns the same rows into RESULTS.
  // SUGGESTED used to be the at-rest state instead: four image cards that
  // faded in 0.5s after load and stepped aside while the field had focus.
  const [searchFocused, setSearchFocused] = useState(false);
  // Set once the picks are final and their list has been on screen, hidden,
  // for a frame (the effect after suggestedPicks, below), so a press into
  // the field before then never shows a list that re-orders under the reader.
  const [picksReady, setPicksReady] = useState(false);
  const normalizedQuery = homeQuery.trim().toLowerCase();
  // RESULTS filter live, with every keystroke (the owner: "while typing, the
  // same list filters live"). Ranked by utils/searchHits, the search
  // overlay's own ranking, so the two cannot disagree about what a term
  // matches; only the overlay waits for a term to settle.
  const suggestions = useMemo(() => rankHits(data, normalizedQuery, SUGGEST_ROWS), [data, normalizedQuery]);
  // The row the arrow keys (or the pointer) have lit: an index into the
  // list's rows, SEE ALL RESULTS included, or -1 for none (the field).
  const [activeRow, setActiveRow] = useState(-1);
  const searchFormRef = useRef(null);
  // The fill that runs before the page moves on (utils/searchFill): the rule
  // and the term on a submit, the rule and the item's name when a row of the
  // list is opened. Set on the column, so the rule, the term and the row all
  // read the same --search-fill.
  const searchColRef = useRef(null);
  const cancelFillRef = useRef(null);
  useEffect(() => () => cancelFillRef.current?.(), []);
  // The row being opened, filling (-1 for none). The list stays up for it.
  const [rowFilling, setRowFilling] = useState(-1);

  // Same fetch as the brand page: fetchItems dedupes in flight and serves
  // the warm cache first, so a return visit paints from the cache at once.
  useEffect(() => {
    let active = true;
    if (!getCachedItems()) setDataLoading(true);
    fetchItems()
      .then((items) => {
        if (!active) return;
        setData(items);
        setDataLoading(false);
      })
      .catch(() => {
        if (active) setDataLoading(false);
      });
    return () => { active = false; };
  }, []);

  // Sold stock and brand backgrounds feed the tiles only; neither holds the
  // page up, and either failing just leaves plainer tiles.
  useEffect(() => {
    let active = true;
    fetchSoldItems()
      .then((items) => { if (active) setSoldData(items || []); })
      .catch(() => {});
    fetchBrandBackgrounds()
      .then((map) => {
        if (!active) return;
        setBrandBgs(map || {});
        // Nothing hidden if the list could not be read.
        setHiddenBrands(getShowcaseHiddenBrands() || new Set());
      })
      .catch(() => { if (active) setHiddenBrands(new Set()); });
    return () => { active = false; };
  }, []);

  // The header's loading bar runs while the inventory is fetched, as on /brand.
  useEffect(() => {
    setLoading(dataLoading);
    return () => setLoading(false);
  }, [dataLoading, setLoading]);

  // Idle-time warmup of the brand backgrounds and product images, so the
  // catalog and brand pages paint near-instant when the visitor moves on.
  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    const handle = idle(() => warmupAll());
    return () => {
      if (window.cancelIdleCallback && typeof handle === "number") {
        window.cancelIdleCallback(handle);
      }
      cancelWarmup();
    };
  }, []);

  const arrivals = useMemo(() => {
    if (!data || !data.length) return [];
    return [...data]
      .sort((a, b) => listedAt(b) - listedAt(a))
      .slice(0, NEW_ARRIVALS_COUNT);
  }, [data]);

  // SUGGESTED, under the search bar when nothing is typed: the first six of
  // the catalog in the catalog's own default order — exactly what /featured
  // opens with. That order is "Most Popular": the page-views ranking, with the
  // API's order as the fallback, the same endpoint and the same Map the
  // catalog builds (item id -> rank index). Same filter too, so a showcase
  // item never appears here any more than it does there.
  // SUGGESTED waits for this ranking (picksReady, below), so its rows never
  // change under the reader once shown. It settles once: with the ranking,
  // or empty (the catalog's order) when the request fails or outlasts
  // PICKS_RANKING_CAP_MS. A ranking turning up after that is dropped.
  const [popularRanking, setPopularRanking] = useState(null);
  useEffect(() => {
    let active = true;
    const settle = (ranking) => {
      if (!active) return;
      active = false;
      setPopularRanking(ranking);
    };
    const cap = setTimeout(() => settle(new Map()), PICKS_RANKING_CAP_MS);
    dummyFetch("/api/views/popular?limit=200&days=30")
      .then((res) => (res && res.ok ? res.json() : null))
      .then((list) => {
        const ranking = new Map();
        if (Array.isArray(list)) list.forEach((entry, idx) => ranking.set(entry.item_id, idx));
        settle(ranking);
      })
      .catch(() => settle(new Map()));
    return () => {
      active = false;
      clearTimeout(cap);
    };
  }, []);
  const suggestedPicks = useMemo(() => {
    if (!data || !data.length) return [];
    const live = data.filter((item) => item.state === "LIVE" && item.is_available !== false && !item.sold && !item.showcase);
    const rankOf = (item) => (popularRanking && popularRanking.has(item.id) ? popularRanking.get(item.id) : Infinity);
    return live
      .map((item, index) => ({ item, index }))
      .sort((a, b) => (rankOf(a.item) - rankOf(b.item)) || (a.index - b.index))
      .slice(0, SUGGEST_ROWS)
      .map(({ item }) => item);
  }, [data, popularRanking]);

  // SUGGESTED is ready to come in once its rows are final (the inventory in,
  // the ranking settled) and its list has been painted hidden for a frame:
  // one mounted already shown has no "before" for its fade to run from.
  const picksFinal = suggestedPicks.length > 0 && popularRanking !== null;
  useEffect(() => {
    if (picksReady || !picksFinal) return undefined;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setPicksReady(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [picksReady, picksFinal]);

  // The brands with the most live stock, as image tiles when they have a
  // still background (a video background keeps a plain tile). Ties break
  // alphabetically so the row is stable between loads.
  const brandTiles = useMemo(() => {
    if (!data || !data.length || !hiddenBrands) return [];
    const counts = new Map();
    for (const item of data) {
      if (!item.brand_id) continue;
      const name = (item.brand_name || "").trim();
      if (!name || hiddenBrands.has(name.toLowerCase())) continue;
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, count]) => {
        const bg = brandBgs[name.toLowerCase()];
        return { name, count, image: bg && bg.type !== "video" ? bg.url : null };
      });
  }, [data, brandBgs, hiddenBrands]);

  // One tile per category that has stock, fronted by its newest item.
  const categoryTiles = useMemo(() => {
    if (!data || !data.length) return [];
    return CATEGORY_GROUPS
      .map((group) => {
        const items = data
          .filter((item) => categoryOf(item) === group.key)
          .sort((a, b) => listedAt(b) - listedAt(a));
        return {
          key: group.key,
          label: group.label,
          count: items.length,
          image: items.length ? coverOf(items[0]) : null,
        };
      })
      .filter((tile) => tile.count > 0);
  }, [data]);

  const soldTile = useMemo(() => {
    const sorted = [...soldData].sort((a, b) => soldAt(b) - soldAt(a));
    return { count: sorted.length, image: sorted.length ? coverOf(sorted[0]) : null };
  }, [soldData]);

  // Suggestions push the page down instead of covering it. The panel stays
  // absolutely positioned (so the bar itself never moves), but however far it
  // hangs past the search block's bottom edge is measured and handed to the
  // block as --suggest-h, which app.css adds to the block's bottom margin —
  // so NEW ARRIVALS moves down by exactly that and the gap between the two
  // holds. A ResizeObserver on both keeps it right as matches come and go and
  // on resize; the margin change cannot feed back, since a margin is outside
  // the rect being measured. A callback ref, so it needs no dependency on
  // whatever state mounts the panel.
  const suggestObserver = useRef(null);
  const attachSuggest = useCallback((node) => {
    if (suggestObserver.current) {
      suggestObserver.current.disconnect();
      suggestObserver.current = null;
    }
    const block = document.querySelector(".home-search");
    if (!block) return;
    if (!node) {
      block.style.setProperty("--suggest-h", "0px");
      return;
    }
    const measure = () => {
      const over = node.getBoundingClientRect().bottom - block.getBoundingClientRect().bottom;
      block.style.setProperty("--suggest-h", Math.max(0, Math.ceil(over)) + "px");
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    ro.observe(block);
    suggestObserver.current = ro;
  }, []);
  useEffect(() => () => suggestObserver.current?.disconnect(), []);

  // Pressing a hub section itself — its black, not a card, tile or field —
  // centres that section in the viewport: a quick way to move between the
  // shelves. It used to light the box a shade for a beat as well; that grey
  // flash was taken out, so the scroll is the whole response now.
  // pressSection is the press itself; the click handler below and the brands
  // row's wheel both call it. Declared above the effects that use it: an
  // effect's dependency list is read during render, so a const declared
  // further down would still be in its temporal dead zone there.
  const pressSection = useCallback((el) => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" });
  }, []);
  // Anything that is its own control keeps its click; the section is whichever
  // element the handler is mounted on.
  const handleSectionPress = useCallback((e) => {
    // Pointer devices only. On a touchscreen a tap on the gaps between cards
    // is not aimed at the section, and snapping the page to centre it under
    // a reading thumb was a jolt, not a shortcut.
    if (isTouchDevice()) return;
    if (e.target.closest("a, button, input, textarea, [role=button], [contenteditable]")) return;
    pressSection(e.currentTarget);
  }, [pressSection]);

  // Wheel-session gate for the brands row (below): the timestamp of the last
  // consumed wheel tick over it.
  const brandsLastWheelRef = useRef(0);

  // The brands row scrolls sideways — there are more brands than fit across.
  // A vertical wheel drives it horizontally, which is what makes it usable
  // with an ordinary mouse; trackpad, drag and touch already work natively.
  //
  // Attached by hand rather than with onWheel because the handler has to
  // preventDefault, and React's wheel listener is passive. At either end we
  // bow out without preventing, so the row hands the gesture back to the page
  // instead of trapping it — scrolling past the row never gets stuck.
  // Ties a row's heading rule to that row's scroll: the sheen travels with
   // the pieces, so the line reads as belonging to the row rather than
   // sitting above it.
  // Written straight to the section's style as a custom property — a scroll
  // fires on every frame of a drag, and re-rendering a row of cards per frame
  // would cost far more than the effect is worth.
  const trackSheen = useCallback((el) => {
    if (!el) return;
    const section = el.closest(".home-hub__section");
    if (!section) return;
    const span = el.scrollWidth - el.clientWidth;
    // A row that does not overflow has no progress to report; leave the sheen
    // where it starts rather than dividing by zero.
    const pos = span > 0 ? (el.scrollLeft / span) * 100 : 0;
    section.style.setProperty("--row-sheen", pos + "%");
  }, []);

  const [showcaseOpen, setShowcaseOpen] = useState(false);
  // Whether the brands past the first row are in the grid. Separate from
  // showcaseOpen, which the button's label and arrow follow, so that SEE LESS
  // can fade them out and fold the grid shut before they leave the DOM.
  const [showcaseMounted, setShowcaseMounted] = useState(false);
  const showcaseGridRef = useRef(null);
  const showcaseAnimsRef = useRef([]);
  const isPhone = useMediaQuery("(max-width: 767px)");
  const SHOWCASE_COLLAPSED = isPhone ? SHOWCASE_COLLAPSED_PHONE : SHOWCASE_COLLAPSED_WIDE;
  // SHOWCASE's and SHOP BY's tiles fade in left to right the first time
  // each row comes into view (the owner, 2026-10-17).
  const showcaseReveal = useTileReveal(showcaseGridRef, brandTiles.length > 0, Math.min(brandTiles.length, SHOWCASE_COLLAPSED));
  const shopGridRef = useRef(null);
  const shopReveal = useTileReveal(
    shopGridRef,
    categoryTiles.length > 0 || soldTile.count > 0,
    categoryTiles.length + (soldTile.count > 0 ? 1 : 0),
  );
  // SEE MORE / SEE LESS. Opening grows the grid to its new height while the
  // brands it brings fade and rise in, one after another; closing fades them
  // out, then folds the grid back to its first row. Web Animations on the
  // grid's height and on the tiles, so nothing re-renders mid-motion. A press
  // during either starts from wherever the grid is rather than jumping.
  // Instant under prefers-reduced-motion.
  const toggleShowcase = useCallback(() => {
    const grid = showcaseGridRef.current;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const from = grid ? grid.getBoundingClientRect().height : 0;
    showcaseAnimsRef.current.forEach((a) => a.cancel());
    showcaseAnimsRef.current = [];
    if (!grid || reduce) {
      const next = !showcaseOpen;
      setShowcaseOpen(next);
      setShowcaseMounted(next);
      return;
    }
    const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
    grid.style.overflow = "hidden";
    if (!showcaseOpen) {
      flushSync(() => {
        setShowcaseOpen(true);
        setShowcaseMounted(true);
      });
      const to = grid.getBoundingClientRect().height;
      const grow = grid.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 520, easing: EASE });
      const fades = [...grid.children].slice(SHOWCASE_COLLAPSED).map((el, i) => el.animate(
        [{ opacity: 0, transform: "translateY(16px)" }, { opacity: 1, transform: "none" }],
        { duration: 460, delay: 90 + i * 60, easing: EASE, fill: "backwards" },
      ));
      showcaseAnimsRef.current = [grow, ...fades];
      grow.finished.then(() => { grid.style.overflow = ""; }, () => {});
      return;
    }
    // Closing: the label turns now; the tiles stay until the fold is done.
    flushSync(() => setShowcaseOpen(false));
    const lastKept = grid.children[SHOWCASE_COLLAPSED - 1];
    const to = lastKept ? lastKept.getBoundingClientRect().bottom - grid.getBoundingClientRect().top : 0;
    const fades = [...grid.children].slice(SHOWCASE_COLLAPSED).map((el) => el.animate(
      [{ opacity: 1 }, { opacity: 0 }],
      { duration: 220, easing: "ease", fill: "forwards" },
    ));
    const fold = grid.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 480, delay: 140, easing: EASE, fill: "both" });
    showcaseAnimsRef.current = [fold, ...fades];
    fold.finished.then(() => {
      flushSync(() => setShowcaseMounted(false));
      grid.style.overflow = "";
      fold.cancel();
      fades.forEach((a) => a.cancel());
      showcaseAnimsRef.current = [];
    }, () => {});
  }, [showcaseOpen, SHOWCASE_COLLAPSED]);
  const brandsScrollRef = useRef(null);
  useEffect(() => {
    const el = brandsScrollRef.current;
    if (!el) return;
    const glider = createGlider(el);
    const onWheel = (e) => {
      // Honour a real horizontal gesture (trackpad) over the vertical axis.
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!delta) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft >= max - 1;
      if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;
      e.preventDefault();
      glider.nudge(delta);
      // A wheel over the row also wakes its section: the first tick of a
      // wheel session (400ms of silence before it) centres BRANDS in the
      // viewport and lights it — the same press a click gives — so wheeling
      // onto the shelf both scrolls it and brings it to you. Only on a
      // consumed tick: a wheel that bowed out above is the page scrolling
      // past, and pulling the section back under it would fight that.
      const now = Date.now();
      if (now - brandsLastWheelRef.current > 400) {
        const section = el.closest(".home-hub__section");
        if (section) pressSection(section);
      }
      brandsLastWheelRef.current = now;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [brandTiles.length, pressSection]);

  // NEW ARRIVALS is a slow carousel that never ends. The twelve newest
  // pieces drift left at a steady pace (ARRIVALS_SECONDS_PER_CARD a card),
  // and the row renders them twice so it can loop: each time the first set
  // has gone by, it steps back by exactly one set, which puts the second set
  // where the first was, so the join never shows and nothing rewinds. The
  // cards cross at full size, in from the right edge and out at the left,
  // with nothing on the way: the arc they dipped into at the right edge (a
  // scale and fade, scroll-driven, in app.css) is gone. It moves the row's
  // own scroll position, so the row carries SELF_SCROLL_ATTR: page-wide "the
  // visitor is scrolling" listeners leave its scroll out (utils/selfScroll).
  // No controls: no wheel, drag, pager or scrollbar, and the row cannot be
  // moved by hand (overflow hidden). It floats all the time, the mouse over
  // it included (the owner, 2026-10-17: "dont stop on hover, just provide
  // arrows on either side of the carousel allowing us to scroll 2 items
  // right or left"): under the pointer the row shows an arrow at each end,
  // and a press glides it two cards that way (nudge), after which the drift
  // carries on. It keeps going while any of it is on screen, the peek under
  // the landing included, and rests only while the tab is hidden, the row is
  // wholly off screen, or a finger is on it. Under prefers-reduced-motion it
  // does not move: one set, scrolled by hand (--manual), the arrows
  // scrolling it two cards.
  const arrivalsReduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  // Five or more to loop: the widest row shows 4.75 cards, and one set has to
  // be wider than the view or the join would show as a gap.
  const arrivalsLoop = !arrivalsReduceMotion && arrivals.length >= 5;
  const arrivalsScrollRef = useRef(null);
  // The arrows' way into the drift (set by the effect below while it runs).
  const arrivalsNudgeRef = useRef(null);
  useEffect(() => {
    const el = arrivalsScrollRef.current;
    if (!el || !arrivalsLoop) return undefined;
    // The heading rule's sheen rides the drift, once across per set. Its
    // gradient repeats every 200% (background-size in app.css), so stepping
    // back a set leaves it exactly where it was. Written to the title row,
    // not the section as trackSheen does for the brands row: a custom
    // property set on the section restyles every card under it, and this
    // one changes on every frame.
    const titleRow = el.closest(".home-hub__section")?.querySelector(".brand-row__title-row");
    // Card pitch, the width of one set and the row's own width, kept current
    // as the row resizes, so a frame reads no layout.
    let pitch = 0;
    let loop = 0;
    let width = 0;
    const measure = () => {
      const cards = el.children;
      const n = arrivals.length;
      pitch = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : 0;
      loop = cards.length > n ? cards[n].offsetLeft - cards[0].offsetLeft : 0;
      width = el.clientWidth;
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    let pos = el.scrollLeft;
    let last = 0;
    let raf = 0;
    // An arrow press in flight: from where to where, since when. Then the
    // rest after it (until resumeAt) and the ramp back up (until rampEnd).
    let nudge = null;
    let resumeAt = 0;
    let rampEnd = 0;
    // A finger on the row holds it too, the touchscreen's version of the
    // mouse hold: set on pointerdown, cleared ARRIVALS_TOUCH_RESUME_MS after
    // the finger lifts (or a page scroll takes the touch over), so the row
    // never slides off under a lifting finger.
    let pressed = false;
    let pressTimer = 0;
    let onScreen = true;
    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const dt = last ? Math.min(64, now - last) : 0;
      last = now;
      // A set narrower than the view cannot loop without a gap; hold.
      if (pitch <= 0 || loop <= width) return;
      // Anything else that moved the row (a focused card scrolled into view)
      // is where the drift carries on from.
      if (Math.abs(el.scrollLeft - pos) > 2) pos = el.scrollLeft;
      if (nudge) {
        // An arrow's glide, eased out, then back to the drift.
        const t = Math.min(1, (now - nudge.start) / ARRIVALS_NUDGE_MS);
        pos = nudge.from + (nudge.to - nudge.from) * (1 - (1 - t) ** 3);
        if (t >= 1) {
          nudge = null;
          resumeAt = now + ARRIVALS_NUDGE_REST_MS;
          rampEnd = resumeAt + ARRIVALS_RAMP_MS;
        }
      } else if (pressed || !onScreen || document.hidden || now < resumeAt) {
        // Held: on the nearest whole pixel, where the cards draw sharp,
        // rather than a fraction of one (the translate below).
        if (pos === Math.round(pos)) return;
        pos = Math.round(pos);
      } else {
        // Back up to pace after an arrow's rest, eased, from half of it.
        let pace = 1;
        if (now < rampEnd) {
          const t = (now - resumeAt) / ARRIVALS_RAMP_MS;
          pace = ARRIVALS_RAMP_FROM + (1 - ARRIVALS_RAMP_FROM) * t * t * (3 - 2 * t);
        }
        pos += (pitch / (ARRIVALS_SECONDS_PER_CARD * 1000)) * dt * pace;
      }
      // Round the loop either way (back past the start lands in the second
      // set, which looks the same), a glide in flight shifted with it.
      const wrap = pos >= loop ? -loop : pos < 0 ? loop : 0;
      if (wrap) {
        pos += wrap;
        if (nudge) {
          nudge.from += wrap;
          nudge.to += wrap;
        }
      }
      // The browser keeps a scroll position to whole pixels, which at this
      // pace moves the row in one-pixel ticks a few frames apart. The row
      // scrolls to the whole pixel and the cards take up the fraction as a
      // translate of under a pixel, so they glide. Only the
      // cards in view, and one either side: restyling all of them every
      // frame tripled the drift's style work, and one off screen catches up
      // on the frame it comes in.
      const whole = Math.floor(pos);
      el.scrollLeft = whole;
      const rest = (whole - pos).toFixed(2) + "px";
      const cards = el.children;
      const end = Math.min(cards.length, Math.ceil((pos + width) / pitch) + 1);
      for (let i = Math.max(0, Math.floor(pos / pitch) - 1); i < end; i++) cards[i].style.translate = rest;
      titleRow?.style.setProperty("--row-sheen", (pos / loop) * 200 + "%");
    };
    // The loop runs only while the row is on screen (the observer below):
    // even a frame that changes nothing wakes the page, which a phone pays
    // for in battery. A restart has no previous frame, so its first step is
    // zero rather than the time spent away.
    const start = () => {
      if (raf) return;
      last = 0;
      raf = requestAnimationFrame(frame);
    };
    const stop = () => {
      cancelAnimationFrame(raf);
      raf = 0;
    };
    start();
    // An arrow: two cards back (-1) or on (1). A second press while the
    // first is gliding goes on from where that one is headed.
    // It lands on a card's edge, the three in the reel square on the page's
    // margins (the owner, 2026-10-17: "always land properly on the margin
    // for the 3 items in the reel"), not two cards on from wherever the
    // drift had got to: the nearest whole card two along. A whole card is
    // a whole number of pitches, and so is the loop, so the wrap keeps it.
    arrivalsNudgeRef.current = (dir) => {
      if (pitch <= 0) return;
      const base = nudge ? nudge.to : pos;
      const to = Math.round(base / pitch + dir * ARRIVALS_NUDGE_CARDS) * pitch;
      nudge = { from: pos, to, start: performance.now() };
      start();
    };
    // Touch and pen: the press (pressed, above). The release is heard on the
    // window, since a finger often lifts somewhere off the row.
    const onPress = (e) => {
      if (e.pointerType === "mouse") return;
      pressed = true;
      clearTimeout(pressTimer);
    };
    const onRelease = (e) => {
      if (e.pointerType === "mouse" || !pressed) return;
      clearTimeout(pressTimer);
      pressTimer = setTimeout(() => { pressed = false; }, ARRIVALS_TOUCH_RESUME_MS);
    };
    el.addEventListener("pointerdown", onPress);
    window.addEventListener("pointerup", onRelease);
    window.addEventListener("pointercancel", onRelease);
    const io = typeof IntersectionObserver !== "undefined"
      ? new IntersectionObserver(([entry]) => {
        onScreen = entry.isIntersecting;
        if (onScreen) start();
        else stop();
      }, { threshold: 0 })
      : null;
    io?.observe(el);
    return () => {
      stop();
      arrivalsNudgeRef.current = null;
      clearTimeout(pressTimer);
      ro?.disconnect();
      io?.disconnect();
      el.removeEventListener("pointerdown", onPress);
      window.removeEventListener("pointerup", onRelease);
      window.removeEventListener("pointercancel", onRelease);
      titleRow?.style.removeProperty("--row-sheen");
      for (const card of el.children) card.style.translate = "";
    };
  }, [arrivals.length, arrivalsLoop]);

  // The arrows beside the row: into the drift when it runs, otherwise (the
  // reduced-motion row, scrolled by hand) a smooth scroll of two cards.
  const nudgeArrivals = useCallback((dir) => {
    if (arrivalsNudgeRef.current) {
      arrivalsNudgeRef.current(dir);
      return;
    }
    const el = arrivalsScrollRef.current;
    const cards = el?.children;
    if (!el || !cards || cards.length < 2) return;
    const pitch = cards[1].offsetLeft - cards[0].offsetLeft;
    el.scrollBy({ left: dir * ARRIVALS_NUDGE_CARDS * pitch, behavior: "smooth" });
  }, []);

  // The loop's second set is for the eye: still clickable (it is on screen
  // half the time), but out of the reading order and the tab order. Again
  // for each new list, since a newly arrived piece comes in as a new card.
  useEffect(() => {
    arrivalsScrollRef.current?.querySelectorAll(".home-arrivals__ghost").forEach((card) => {
      card.setAttribute("aria-hidden", "true");
      card.setAttribute("tabindex", "-1");
      card.querySelectorAll("a, button").forEach((c) => c.setAttribute("tabindex", "-1"));
    });
  }, [arrivals, arrivalsLoop]);

  // The catalog's scroll-down caret on the landing (FeaturedPage's
  // .featured-scroll-down: the same button, glyph and drift). It first shows
  // SCROLL_CUE_DELAY_MS into an arrival, and from then on whenever the page
  // is still: any scroll, the visitor's own or a press's, puts it away, and
  // it comes back once the page has been still for SCROLL_CUE_IDLE_MS, for
  // as long as there is page below it. Each press brings the next section
  // up, framed (handleScrollDown). No up caret. On a window short enough to
  // show the NEW ARRIVALS heading at the top of the landing, it sits midway
  // between the search block and that heading, never on it; anywhere else it
  // takes the catalog's spot. A phone's landing fills the screen, so a phone
  // never shows the heading there, and its caret keeps the catalog's own
  // rule (styles/mobile.css). It steps aside while
  // the search has focus (searchFocused), where it would float over the
  // results, and on a phone over the keyboard's edge.
  const [cueArrived, setCueArrived] = useState(false);
  const [hasBelow, setHasBelow] = useState(true);
  const [scrolling, setScrolling] = useState(false);
  const scrollCueRef = useRef(null);
  useEffect(() => {
    const phone = window.matchMedia("(max-width: 767px)");
    const place = () => {
      const cue = scrollCueRef.current;
      const hero = cue?.parentElement.querySelector(".home-search");
      if (!cue || !hero) return;
      // The catalog's spot: 20px up from the bottom (12px on a phone), plus
      // half the 26px button, to the caret's centre.
      // Always there now (the owner, 2026-10-17: "arrow location is broken
      // put it at bottom"). It used to rise to midway between the search
      // and the NEW ARRIVALS heading whenever the heading showed, which,
      // with the heading peeking half off the bottom edge, left it in the
      // middle of the screen. The heading starts at the left edge and the
      // caret is centred, so the two do not meet.
      const y = window.innerHeight - (phone.matches ? 12 : 20) - 13;
      cue.style.setProperty("--home-cue-y", y + "px");
    };
    const measure = () => {
      // Up only while there is page below before the footer (utils/scrollCue).
      setHasBelow(hasMoreBelow());
      place();
    };
    // The page is at least a screen tall, so a short one still puts the
    // footer at the bottom (its margin-top: auto). It used to run on a whole
    // screen past where a press lands the last section, so SHOP BY could be
    // framed at the top like the others, which left a screen of black
    // between SHOP ALL and the footer (the owner, 2026-10-17: "close this
    // gap between shop all and footer"). The arrow's last press now ends at
    // the bottom of the page (handleScrollDown).
    const fitTail = () => {
      const root = scrollCueRef.current?.parentElement;
      if (!root) return;
      const need = Math.ceil(window.innerHeight) + "px";
      if (root.style.minHeight !== need) root.style.minHeight = need;
    };
    const onLayout = () => {
      fitTail();
      measure();
    };
    let stillTimer = null;
    let raf = 0;
    const onScroll = () => {
      setScrolling(true);
      clearTimeout(stillTimer);
      stillTimer = setTimeout(() => setScrolling(false), SCROLL_CUE_IDLE_MS);
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    onLayout();
    const arrive = setTimeout(() => setCueArrived(true), SCROLL_CUE_DELAY_MS);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onLayout) : null;
    ro?.observe(document.body);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onLayout);
    return () => {
      clearTimeout(arrive);
      clearTimeout(stillTimer);
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onLayout);
    };
  }, []);
  const downShown = cueArrived && hasBelow && !scrolling && !searchFocused;

  // One press brings the next section up to where a section is read from:
  // its title SECTION_TITLE_INSET under the header's height, whatever point
  // the page was left at. Past the last section it goes to the bottom of the
  // page. The header's height, not where it is now: it scrolls away on the
  // way down but comes back on the way up, and would land on the title.
  // A fixed jump (85% of a screen, as on the catalog) stopped wherever it
  // stopped, cutting a section off at an arbitrary point.
  const handleScrollDown = useCallback(() => {
    const root = scrollCueRef.current?.parentElement;
    if (!root) return;
    const headerH = document.querySelector(".app-header")?.offsetHeight ?? 0;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    // A section already within 24px of its spot counts as the one in view.
    const next = [...root.querySelectorAll(".home-hub__section .brand-row__title")]
      .map((t) => t.getBoundingClientRect().top + window.scrollY - headerH - SECTION_TITLE_INSET)
      .find((y) => y > window.scrollY + 24);
    window.scrollTo({ top: Math.min(max, next ?? max), behavior: "smooth" });
  }, []);

  // A brand tile lands on that brand: the same channel the sidebar's brand
  // list uses, so the carousel scrolls to it on arrival.
  const goToBrand = useCallback((name) => {
    requestBrandNav(name);
    navigate("/showcase");
  }, [navigate]);

  // The list's rows, in order: SUGGESTED's picks while the field is empty,
  // the live RESULTS once a term is typed, and then SEE ALL RESULTS, the way
  // on to the catalog's search. The arrow keys walk them; Enter opens the
  // lit one.
  const listItems = normalizedQuery ? suggestions : suggestedPicks;
  const listRows = [
    ...listItems.map((item) => ({ kind: "item", item, to: `/featured/item/${item.id}` })),
    ...(normalizedQuery && suggestions.length > 0 ? [{ kind: "all" }] : []),
  ];
  // The list is up while the search has focus and has something to say:
  // RESULTS (or No matches) once a term is typed, SUGGESTED once its rows are
  // final. Not while the rule fills on a submit.
  const suggestOpen = (searchFocused || rowFilling >= 0) && !searchFilling && (normalizedQuery.length > 0 || (picksReady && suggestedPicks.length > 0));
  // A new term, or the list going away, starts the highlight over.
  useEffect(() => {
    setActiveRow(-1);
  }, [normalizedQuery, suggestOpen]);
  const suggestListRef = useRef(null);
  // Steps the highlight through the rows on screen (a phone shows fewer, so
  // the hidden ones are skipped). Off either end goes back to the field.
  const moveActiveRow = (dir) => {
    const rows = [...(suggestListRef.current?.querySelectorAll("[data-row-index]") || [])].filter((el) => el.offsetParent !== null);
    if (!rows.length) return;
    const at = rows.findIndex((el) => Number(el.dataset.rowIndex) === activeRow);
    const next = at < 0 ? (dir > 0 ? 0 : rows.length - 1) : at + dir;
    setActiveRow(next < 0 || next >= rows.length ? -1 : Number(rows[next].dataset.rowIndex));
  };
  // Opening a row: SEE ALL submits the term; a piece fills, its name and the
  // rule together, while its first photo loads, then opens.
  const openRow = (index) => {
    const row = listRows[index];
    if (!row || rowFilling >= 0 || searchFilling) return;
    if (row.kind === "all") {
      searchFormRef.current?.requestSubmit();
      return;
    }
    setActiveRow(index);
    setRowFilling(index);
    // The fill runs while the piece's first photo, the one its page opens
    // on, loads (utils/searchFill).
    const firstPhoto = Array.isArray(row.item.photos_json) ? row.item.photos_json[0]?.url : null;
    cancelFillRef.current = runSearchFill(searchColRef.current, preloadImage(firstPhoto), () => navigate(row.to, { state: { from: "/" } }));
  };
  // One row for both lists, SUGGESTED and RESULTS, so the two can never
  // drift: the photo in its 48px square, then the title on one line and
  // the size and the price on the next, with no dot between them (the
  // owner: "no dot"), the size grey and the price white; size is the first
  // thing a resale buyer checks. index becomes --i, the rows' top-to-bottom
  // stagger as the list comes in, and the row's place for the highlight.
  const renderSuggestRow = (item, index = 0) => {
    const image = coverOf(item);
    const price = item.price_usd != null ? formatDisplayPrice(item.price_usd, "USD") : "";
    const size = sizeLabelOf(item.size);
    return (
      <Link
        key={item.id}
        id={`home-suggest-row-${index}`}
        to={`/featured/item/${item.id}`}
        state={{ from: "/" }}
        className={`home-suggest__row${index >= 4 ? " home-suggest__row--extra" : ""}${activeRow === index ? " home-suggest__row--active" : ""}${rowFilling === index ? " home-suggest__row--filling" : ""}`}
        style={{ "--i": index }}
        role="option"
        aria-selected={activeRow === index}
        tabIndex={-1}
        data-row-index={index}
        onMouseEnter={() => { if (rowFilling < 0) setActiveRow(index); }}
        onClick={(e) => {
          // A plain click fills first (openRow); a modified or middle click
          // keeps the link's own behaviour (a new tab, say).
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          openRow(index);
        }}
      >
        <SuggestRowImage url={image} />
        <span className="home-suggest__row-text">
          <span className="home-suggest__row-name">{item.title || "Untitled"}</span>
          {(size || price) && (
            <span className="home-suggest__row-meta">
              {size && <span className="home-suggest__row-size">{size}</span>}
              {price && <span className="home-suggest__row-price">{price}</span>}
            </span>
          )}
        </span>
        {/* At the row's end, SEE ALL RESULTS' arrow: it says the row opens
            the piece (the owner, 2026-10-25: "on these results at the end
            put an arrow"). */}
        <svg className="home-suggest__all-arrow home-suggest__row-arrow" width="15" height="12" viewBox="0 0 15 12" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <path d="M1 6h12.5M9 1l5 5-5 5" />
        </svg>
      </Link>
    );
  };
  // SEE ALL RESULTS, last once something is typed: submits the term, so it
  // goes where Enter with nothing lit goes (the catalog's search), through
  // the same filling rule. The arrow is the showcase pager's forward arrow.
  const renderSeeAll = (index) => (
    <button
      key="all"
      type="button"
      id={`home-suggest-row-${index}`}
      className={`home-suggest__row home-suggest__row--all${activeRow === index ? " home-suggest__row--active" : ""}`}
      style={{ "--i": index }}
      role="option"
      aria-selected={activeRow === index}
      tabIndex={-1}
      data-row-index={index}
      onMouseEnter={() => { if (rowFilling < 0) setActiveRow(index); }}
      onClick={() => openRow(index)}
    >
      <span>See all results</span>
      <svg className="home-suggest__all-arrow" width="15" height="12" viewBox="0 0 15 12" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M1 6h12.5M9 1l5 5-5 5" />
      </svg>
    </button>
  );

  return (
    <div className="home-arrivals-page home-hub">
      {/* The page's own search, given the room a landing page's search gets —
          centred, above everything, with the arrivals reading as what is
          below it rather than as the page's opening.
          It submits to the same place the header's search modal does, so
          there is one search results route however you got there. */}
      <section className="home-search" aria-label="Search" onClick={handleSectionPress}>
        {/* The column is what the suggestions hang from. Without it they
            would anchor to the nearest positioned ancestor — the page — and
            top: 100% would put them below the whole viewport. */}
        <div
          ref={searchColRef}
          className="home-search__col"
          onFocus={() => setSearchFocused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setSearchFocused(false);
          }}
        >
        <form
          ref={searchFormRef}
          className={`home-search__form${searchFilling || rowFilling >= 0 ? " home-search__form--filling" : ""}`}
          onSubmit={(e) => {
            e.preventDefault();
            const term = homeQuery.trim();
            if (searchFilling) return;
            if (!term) return;
            if (rowFilling >= 0) return;
            setSearchFilling(true);
            // The fill runs while the catalog's data loads and finishes when
            // it has, so the results open painted (utils/searchFill).
            cancelFillRef.current = runSearchFill(searchColRef.current, preloadCatalog(), () => navigate(`/featured/search?q=${encodeURIComponent(term)}`));
          }}
        >
          {/* While the rule fills, the field is replaced by the term itself,
              filling in step with it — the same gradient-clipped text the
              sidebar and the search overlay use. A span, not the input: an
              <input> cannot carry background-clip: text, so the letters could
              not be painted by the same sweep. */}
          {searchFilling ? (
            <span className="home-search__filling" aria-live="polite">{homeQuery.trim()}</span>
          ) : (
          <input
            type="search"
            className="home-search__input"
            placeholder="Search"
            aria-label="Search the catalog"
            value={homeQuery}
            autoComplete="off"
            spellCheck="false"
            onChange={(e) => setHomeQuery(e.target.value)}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={suggestOpen}
            aria-controls="home-suggest-list"
            aria-activedescendant={suggestOpen && activeRow >= 0 ? `home-suggest-row-${activeRow}` : undefined}
            onKeyDown={(e) => {
              // Escape puts the list away, as it closes any dropdown.
              // While a piece fills on its way out, the keys wait.
              if (rowFilling >= 0) { e.preventDefault(); return; }
              if (e.key === "Escape") { e.currentTarget.blur(); return; }
              if (!suggestOpen) return;
              // The arrows walk the rows; Enter on a lit row opens it. With
              // nothing lit, Enter submits as it always has.
              if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                moveActiveRow(e.key === "ArrowDown" ? 1 : -1);
              } else if (e.key === "Enter" && activeRow >= 0) {
                e.preventDefault();
                openRow(activeRow);
              }
            }}
          />
          )}
          <button type="submit" className="home-search__go" aria-label="Search">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <circle cx="11" cy="11" r="7" />
              <line x1="16.5" y1="16.5" x2="21" y2="21" />
            </svg>
          </button>
        </form>

        {/* The list under the bar: the search's focus state. SUGGESTED while
            the field is empty, RESULTS once a term is typed, the same rows
            either way. Mounted from the start, only unseen, so it fades in
            on focus rather than popping, and fades out when focus leaves the
            column. While it is up it pushes the page (attachSuggest) instead
            of covering NEW ARRIVALS. A press on it keeps the field's focus
            (mousedown), so a row's click always lands and the list holds.
            Hidden entirely while the search is submitting: the rule and the
            term are filling, and a list changing underneath that would pull
            the eye off it. */}
        {!searchFilling && (
          <div
            className={`home-suggest${suggestOpen ? " home-suggest--shown" : ""}`}
            ref={suggestOpen ? attachSuggest : null}
            aria-hidden={!suggestOpen}
            onMouseDown={(e) => e.preventDefault()}
          >
            {normalizedQuery.length > 0 && suggestions.length === 0 ? (
              <p className="home-suggest__note">No matches</p>
            ) : (
              <>
                <p className="home-suggest__label">{normalizedQuery ? "Results" : "Suggested"}</p>
                <div
                  ref={suggestListRef}
                  className="home-suggest__rows"
                  id="home-suggest-list"
                  role="listbox"
                  aria-label={normalizedQuery ? "Results" : "Suggested"}
                  onMouseLeave={() => setActiveRow(-1)}
                >
                  {listRows.map((row, i) => (row.kind === "all" ? renderSeeAll(i) : renderSuggestRow(row.item, i)))}
                </div>
              </>
            )}
          </div>
        )}
        </div>
      </section>
      <section
        className="brand-row brand-row--visible home-hub__section"
        onClick={handleSectionPress}
      >
        <div className="brand-row__header">
          <div className="brand-row__title-row">
            <h2 className="brand-row__title">New Arrivals</h2>
            {/* The way through to the whole list, the catalog's NEW ARRIVALS
                tab: grey and small, in the showcase's small type, so it reads
                as a note on the heading rather than a second heading. */}
            <Link to="/featured/new" className="home-section-more">See more</Link>
          </div>
        </div>
        {arrivals.length > 0 && (
          <div className="home-arrivals__viewport">
          <div
            className={`home-arrivals__grid${arrivalsReduceMotion ? " home-arrivals__grid--manual" : ""}`}
            ref={arrivalsScrollRef}
            // While it loops, the drift writes the heading's sheen itself and
            // the row is marked as scrolling itself (the arrivals effect).
            onScroll={arrivalsLoop ? undefined : (e) => trackSheen(e.currentTarget)}
            {...(arrivalsLoop ? { [SELF_SCROLL_ATTR]: "" } : null)}
          >
            {arrivals.map((item, index) => (
              // The catalog's own card (components/ProductCard), so the two
              // cannot drift apart. `state.from` "/" gives the product page a
              // back arrow that returns here.
              <ProductCard
                key={item.id}
                item={item}
                index={index}
                staggerMs={100}
                to={`/featured/item/${item.id}`}
                state={{ from: "/" }}
              />
            ))}
            {/* The same twelve again, for the loop (the arrivals effect): the
                row steps back by one set as the first goes by, so this set is
                where the first was and the join never shows. Only while the
                row loops: not under reduced motion, where it does not move. */}
            {arrivalsLoop && arrivals.map((item, index) => (
              <ProductCard
                key={`${item.id}-loop`}
                item={item}
                index={index}
                staggerMs={100}
                to={`/featured/item/${item.id}`}
                state={{ from: "/" }}
                className="home-arrivals__ghost"
              />
            ))}
          </div>
          {/* Two cards back or on (nudgeArrivals), at the row's two ends,
              shown while the pointer is over it (app.css); the drift does
              not stop for them. Mouse only: touch has no hover to show them
              with. */}
          <button
            type="button"
            className="home-arrivals__arrow home-arrivals__arrow--left"
            aria-label="Previous new arrivals"
            onClick={(e) => { e.stopPropagation(); nudgeArrivals(-1); }}
          >
            <svg width="13" height="27" viewBox="0 0 8 14" fill="none" stroke="currentColor" strokeWidth="1.24" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <path d="M7 1L1 7L7 13" />
            </svg>
          </button>
          <button
            type="button"
            className="home-arrivals__arrow home-arrivals__arrow--right"
            aria-label="Next new arrivals"
            onClick={(e) => { e.stopPropagation(); nudgeArrivals(1); }}
          >
            <svg width="13" height="27" viewBox="0 0 8 14" fill="none" stroke="currentColor" strokeWidth="1.24" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <path d="M1 1L7 7L1 13" />
            </svg>
          </button>
          </div>
        )}
      </section>

      {brandTiles.length > 0 && (
        <section className="brand-row brand-row--visible home-hub__section home-hub__section--tiles" onClick={handleSectionPress}>
          <div className="brand-row__header">
            <div className="brand-row__title-row">
              <h2 className="brand-row__title">Showcase</h2>
              {/* Same note as NEW ARRIVALS' SEE MORE: the whole showcase. */}
              <Link to="/showcase" className="home-section-more">View all</Link>
            </div>
            <div className="brand-row__count-bar">
              <span className="brand-row__count">{" "}</span>
            </div>
          </div>
          {/* A wrapping grid, not a scroller: the rest of the brands arrive by
              opening the section rather than by dragging sideways past them. */}
          <div className={`home-tiles home-tiles--showcase${showcaseReveal}`} ref={showcaseGridRef}>
            {(showcaseMounted ? brandTiles : brandTiles.slice(0, SHOWCASE_COLLAPSED)).map((brand, index) => (
              <Tile
                key={brand.name}
                index={index}
                onClick={() => goToBrand(brand.name)}
                image={brand.image}
                label={brand.name}
                // The name alone, no product count (the owner, 2026-10-17).
              />
            ))}
            {/* Last cell in the scroller: the way through to the showcase.
                Grey rather than a photograph so it reads as a control and not
                as one more tile; ALL BRANDS in white in the bottom-left
                corner, where a brand's name sits. */}
            {showcaseMounted && (
              /* Grey rather than a photograph so it reads as a control and not
                 as one more brand; only once the grid is open, where it is the
                 end of the set rather than a lid on it. */
              <Link to="/showcase" className="home-tile home-tile--plain home-tile--all">
                <span className="home-tile__label">All brands</span>
              </Link>
            )}
          </div>
          {brandTiles.length > SHOWCASE_COLLAPSED && (
            <button
              type="button"
              className={`home-showcase-more${showcaseOpen ? " home-showcase-more--open" : ""}`}
              onClick={toggleShowcase}
              aria-expanded={showcaseOpen}
            >
              <span className="home-showcase-more__label">
                {showcaseOpen ? "See less" : "See more"}
              </span>
              {/* One chevron: down while there is more to see, turned up
                  (--open) to fold the section away again. */}
              <span className="home-showcase-more__arrow" aria-hidden="true">
                <svg width="14" height="8" viewBox="0 0 14 8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 1L7 7L13 1" />
                </svg>
              </span>
            </button>
          )}
        </section>
      )}

      {(categoryTiles.length > 0 || soldTile.count > 0) && (
        <section className="brand-row brand-row--visible home-hub__section home-hub__section--tiles" onClick={handleSectionPress}>
          <div className="brand-row__header">
            <div className="brand-row__title-row">
              <h2 className="brand-row__title">Shop by</h2>
            </div>
            <div className="brand-row__count-bar">
              <span className="brand-row__count">{" "}</span>
            </div>
          </div>
          <div className={`home-tiles home-tiles--shop${shopReveal}`} ref={shopGridRef}>
            {categoryTiles.map((tile, index) => (
              <Tile
                key={tile.key}
                index={index}
                to={`/featured?cat=${tile.key}`}
                image={tile.image}
                product
                label={tile.label}
                sub={plural(tile.count, "Product", "Products")}
              />
            ))}
            {soldTile.count > 0 && (
              <Tile
                to="/featured/sold"
                index={categoryTiles.length}
                image={soldTile.image}
                product
                label="Sold Archive"
                sub={`${soldTile.count} sold`}
              />
            )}
          </div>
        </section>
      )}

      <div className="home-arrivals__actions">
        <Link to="/featured" className="home-arrivals__shop">Shop all</Link>
      </div>
      {/* The catalog's scroll-down caret, on the landing (the scroll-cue
          effect): at the foot of the screen whenever the page is still and
          there is more below. */}
      <button
        ref={scrollCueRef}
        type="button"
        className={`featured-scroll-down home-scroll-cue${downShown ? " featured-scroll-down--shown" : ""}`}
        aria-label="Scroll down"
        tabIndex={downShown ? 0 : -1}
        aria-hidden={!downShown}
        onClick={handleScrollDown}
      >
        <svg className="featured-scroll-caret__glyph" width="24" height="14" viewBox="0 0 14 8" fill="none" stroke="currentColor"
          strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <path d="M1 1L7 7L13 1" />
        </svg>
      </button>
      <Footer />
    </div>
  );
}
